import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { RenderedPart, renderThrough, type RenderElement } from '@vs-shop/ui/render';
import { clsxm } from '@vs-shop/utils';

/**
 * How rcprofi lays a megamenu panel out.
 *
 * The mechanism — the word, the sliver, the panel, the dismiss control — is
 * `@vs-shop/commerce-ui/megamenu` and is the same in every shop. What is here
 * is the arrangement, and every number in it is this shop's: a wide half beside
 * a 355px aside, three tiles across at `lap` and four at `des`, a tile whose
 * mark sits BESIDE its copy in a 90x77 box, brand names in two columns.
 *
 * A second shop reading the same catalogue arranges it differently, which is
 * why none of this crossed into the shared tier — a shared part carrying one
 * shop's counts reads as portable right up until the second shop arrives.
 */

/* -------------------------------------------------------------------------- */
/* The row                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * How this shop sets a word of the category row.
 *
 * Large, in the case an editor wrote it, inverted on the band and brand-green
 * while its panel is open. The measures are this shop's rhythm, and the row's
 * height falls out of them — which is why the `<nav>` declares
 * `--megamenu-row` to match and the panel hangs at it.
 *
 * `inset` is the first word's affordance: the row's leading item sits clear of
 * the container edge. A part paints its own box, so that inset is an arm here
 * rather than a descendant selector on whatever contains it.
 */
export const megamenuRowWordVariants = cva(
  ['text-lg des-sm:text-xl', 'px-2.5 py-2.5 lap-lg:px-6 des-sm:px-8'],
  {
    variants: {
      variant: {
        /** A catalogue word: inverted on the band, brand-coloured while open. */
        category: 'text-foreground-inverted hover:text-primary data-[state=open]:text-primary',
        /** A word that is announcing something rather than opening a panel. */
        event: 'text-warning',
      },
      inset: {
        /** The leading word, clear of the container edge. */
        leading: [
          'ml-content-safe-padding-sm lap:ml-content-safe-padding-lg',
          // Zeroed at EVERY step the base recipe sets a horizontal padding at,
          // `des-sm` included. The descendant-selector chain this replaces got
          // that for free from specificity; plain utilities are ordered by
          // breakpoint instead, so a step left out here is a step where the
          // padding comes back and the leading word sits one gutter too far in.
          'pl-0 lap-lg:pl-0 des-sm:pl-0 des:pl-0',
        ],
        /** Every other word: the row's own rhythm, nothing added. */
        flush: '',
      },
    },
  }
);

/** The ink of the sliver under a word, which the shared part leaves to the shop. */
export const megamenuRowIndicatorVariants = cva('', {
  variants: {
    variant: {
      /** Under a catalogue word. */
      category: 'bg-primary',
      /** Under an announcement. */
      event: 'bg-warning',
    },
  },
});

/* -------------------------------------------------------------------------- */
/* The frame                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The panel's frame: a wide half, a rule, and a narrow half.
 *
 * The narrow half's measure is declared here once, as `--megamenu-aside`, and
 * the track is that measure plus the inset the aside itself sits at. Both
 * halves read the same two variables, so the track cannot drift from the thing
 * occupying it.
 */
const megamenuFrameClasses = [
  'grid h-full w-full bg-background shadow-xl shadow-foreground/25',
  'pt-8 pb-4',
  '[--megamenu-aside-inset:2rem] [--megamenu-aside:355px] des:[--megamenu-aside-inset:4rem]',
  'lap-lg:grid-cols-[1fr_1px_calc(var(--megamenu-aside)+var(--megamenu-aside-inset))]',
];

export type MegamenuFrameProps = ComponentProps<'div'>;

export const MegamenuFrame = ({ className, ...props }: MegamenuFrameProps) => (
  <div data-slot="megamenu-frame" className={clsxm(megamenuFrameClasses, className)} {...props} />
);

/** The wide half, where the tiles go. */
const megamenuFrameMainClasses = 'mx-2 flex flex-col lap-lg:mr-8 des:mr-10';

export type MegamenuFrameMainProps = ComponentProps<'div'>;

export const MegamenuFrameMain = ({ className, ...props }: MegamenuFrameMainProps) => (
  <div
    data-slot="megamenu-frame-main"
    className={clsxm(megamenuFrameMainClasses, className)}
    {...props}
  />
);

/**
 * The rule between the halves. One element that turns, rather than a pair each
 * hiding at the step the other appears — a pair says nothing about being the
 * same rule, and reads as two decisions to keep in step.
 */
const megamenuFrameRuleClasses = [
  'mx-5 my-4 h-px min-h-max bg-foreground/10',
  'lap-lg:mx-0 lap-lg:my-10 lap-lg:h-auto lap-lg:w-px lap-lg:min-w-max',
];

export type MegamenuFrameRuleProps = ComponentProps<'div'>;

export const MegamenuFrameRule = ({ className, ...props }: MegamenuFrameRuleProps) => (
  <div
    data-slot="megamenu-frame-rule"
    className={clsxm(megamenuFrameRuleClasses, className)}
    {...props}
  />
);

/** The narrow half: brand columns and the article rail under them. */
const megamenuFrameAsideClasses = [
  'mr-5 ml-7 grid grid-cols-2 gap-4',
  'lap-lg:mt-4 lap-lg:ml-(--megamenu-aside-inset) lap-lg:flex lap-lg:flex-col',
];

export type MegamenuFrameAsideProps = ComponentProps<'div'>;

export const MegamenuFrameAside = ({ className, ...props }: MegamenuFrameAsideProps) => (
  <div
    data-slot="megamenu-frame-aside"
    className={clsxm(megamenuFrameAsideClasses, className)}
    {...props}
  />
);

/* -------------------------------------------------------------------------- */
/* The tile field                                                              */
/* -------------------------------------------------------------------------- */

/** The field of tiles. */
const megamenuTilesClasses = ['grid justify-between gap-y-2', 'grid-cols-3 des:grid-cols-4'];

export type MegamenuTilesProps = ComponentProps<'ul'>;

export const MegamenuTiles = ({ className, ...props }: MegamenuTilesProps) => (
  <ul data-slot="megamenu-tiles" className={clsxm(megamenuTilesClasses, className)} {...props} />
);

/**
 * One tile: a mark, a title, and whatever hangs under them.
 *
 * The item owns its list item — every tile in the field is an `<li>` of the
 * same list, whether its whole surface is one link or its parts are three. It
 * also carries `group`, which is what a title set to underline with its surface
 * resolves against.
 */
export const megamenuTileVariants = cva(
  ['group relative flex h-full gap-8 rounded-lg px-5 py-4', 'hover:bg-background hover:shadow-xl'],
  {
    variants: {
      variant: {
        /** Lifts on a tinted shadow, the way a card of content does. */
        tinted: 'hover:ring hover:shadow-foreground/10 hover:ring-foreground/10',
        /** Lifts on a plain one. */
        plain: 'hover:ring hover:ring-foreground/10',
      },
    },
  }
);

export type MegamenuTileProps = ComponentProps<'li'> & {
  readonly variant: 'tinted' | 'plain';
};

export const MegamenuTile = ({ className, variant, ...props }: MegamenuTileProps) => (
  <li
    data-slot="megamenu-tile"
    className={clsxm(megamenuTileVariants({ variant }), className)}
    {...props}
  />
);

/**
 * A tile whose WHOLE surface is one link.
 *
 * Covers the tile rather than wrapping it: a link wrapped around a mark and a
 * title is invalid the moment either of them becomes a link too, and it is the
 * technique that produced the double tab stop this compound exists without.
 * Where it is the tile's only link it carries the tile's `aria-label`; beside a
 * real control it is hidden from assistive technology and takes no tab stop.
 */
const megamenuTileLinkClasses = 'absolute inset-0 rounded-lg';

export type MegamenuTileLinkProps = ComponentProps<'a'> & {
  /** The element the page navigates with. */
  readonly render: RenderElement;
};

export const MegamenuTileLink = ({ className, render, ...props }: MegamenuTileLinkProps) =>
  renderThrough(render, 'megamenu-tile-link', clsxm(megamenuTileLinkClasses, className), props);

/**
 * The tile's mark box, and how what it holds sits in it.
 *
 * The mark is children — a taxon photograph where the catalogue has one, the
 * missing-image placeholder where it does not — so the part fixes the box and
 * the fit rather than taking either as a prop.
 */
export const megamenuTileMarkVariants = cva('flex h-[77px] w-[90px]', {
  variants: {
    variant: {
      /** Imagery filling the box, kept whole. */
      fill: 'shrink-0 [&_img]:h-full [&_img]:w-full [&_img]:object-contain',
      /** A drawing centred in it. */
      center:
        'items-center justify-center [&_svg]:size-[77px] [&_svg]:stroke-1 [&_svg]:text-foreground-muted',
    },
  },
});

export type MegamenuTileMarkProps = ComponentProps<'div'> & {
  readonly variant: 'fill' | 'center';
  /** The element the page navigates with, or absent for a mark that is not a link. */
  readonly render?: RenderElement;
};

export const MegamenuTileMark = ({
  className,
  variant,
  render,
  ...props
}: MegamenuTileMarkProps) => (
  <RenderedPart
    dataSlot="megamenu-tile-mark"
    className={clsxm(megamenuTileMarkVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/** The tile's copy column. */
const megamenuTileBodyClasses = 'flex flex-col';

export type MegamenuTileBodyProps = ComponentProps<'div'>;

export const MegamenuTileBody = ({ className, ...props }: MegamenuTileBodyProps) => (
  <div
    data-slot="megamenu-tile-body"
    className={clsxm(megamenuTileBodyClasses, className)}
    {...props}
  />
);

/** The title and the child list, held tighter to each other than to the rest. */
const megamenuTileHeaderClasses = 'flex flex-col gap-1';

export type MegamenuTileHeaderProps = ComponentProps<'div'>;

export const MegamenuTileHeader = ({ className, ...props }: MegamenuTileHeaderProps) => (
  <div
    data-slot="megamenu-tile-header"
    className={clsxm(megamenuTileHeaderClasses, className)}
    {...props}
  />
);

/** The tile's list of narrower categories. */
const megamenuTileChildrenClasses = 'grid';

export type MegamenuTileChildrenProps = ComponentProps<'ul'>;

export const MegamenuTileChildren = ({ className, ...props }: MegamenuTileChildrenProps) => (
  <ul
    data-slot="megamenu-tile-children"
    className={clsxm(megamenuTileChildrenClasses, className)}
    {...props}
  />
);

/** One row of that list. The item owns its list item, here as everywhere. */
export type MegamenuTileChildProps = ComponentProps<'li'>;

export const MegamenuTileChild = ({ className, ...props }: MegamenuTileChildProps) => (
  <li data-slot="megamenu-tile-child" className={clsxm(className)} {...props} />
);

/* -------------------------------------------------------------------------- */
/* The brand columns                                                           */
/* -------------------------------------------------------------------------- */

/** The brand block in the aside: a name for it, the columns, and a way to all of them. */
const megamenuBrandsClasses = 'flex flex-col gap-2';

export type MegamenuBrandsProps = ComponentProps<'div'>;

export const MegamenuBrands = ({ className, ...props }: MegamenuBrandsProps) => (
  <div data-slot="megamenu-brands" className={clsxm(megamenuBrandsClasses, className)} {...props} />
);

/** The columns themselves — two of them, which is what the aside's measure takes. */
const megamenuBrandListClasses = 'grid grid-cols-2 gap-x-2.5';

export type MegamenuBrandListProps = ComponentProps<'ul'>;

export const MegamenuBrandList = ({ className, ...props }: MegamenuBrandListProps) => (
  <ul
    data-slot="megamenu-brand-list"
    className={clsxm(megamenuBrandListClasses, className)}
    {...props}
  />
);

/** One name in the columns. */
export type MegamenuBrandProps = ComponentProps<'li'>;

export const MegamenuBrand = ({ className, ...props }: MegamenuBrandProps) => (
  <li data-slot="megamenu-brand" className={clsxm(className)} {...props} />
);
